import type { ReactNode } from 'react';
import { cn } from '~/lib/utils';

export type PageContainerSize = 'fluid' | 'wide';

interface PageContainerProps {
	/**
	 * `fluid` (default) — full viewport width; data grids and the dashboard, where every
	 * extra pixel goes to columns. `wide` — centered `max-w-[1600px]`; editor surfaces,
	 * whose fixed side rails + form body stop benefiting from width beyond that.
	 */
	size?: PageContainerSize;
	className?: string;
	children: ReactNode;
}

/**
 * Standard wrapper for every routed page inside the AppShell. Owns the page padding
 * (scales up on large monitors) and the width policy. Deliberately sets no min-height
 * and no background: the shell's `<main>` already provides `bg-background`, and a
 * `min-h-screen` here would exceed the `100vh − 48px` content row by the topbar height,
 * forcing 48px of phantom scroll on every page.
 */
export function PageContainer({ size = 'fluid', className, children }: PageContainerProps) {
	return (
		<div className="p-4 md:p-6 2xl:p-8">
			<div className={cn('w-full', size === 'wide' && 'mx-auto max-w-[1600px]', className)}>{children}</div>
		</div>
	);
}
